import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@shared/services/api';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import type { RestoreJob, RestoreJobsResponse, RestoreJobsFilters } from '../types/restoreJob';

interface UseRestoreJobsReturn {
  jobs: RestoreJob[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  timezone: string;
  fetchJobs: (background?: boolean) => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: RestoreJobsFilters) => void;
}

export interface UseRestoreJobsOptions extends RestoreJobsFilters {
  /**
   * Set false to suspend the background refresh — e.g. while a modal that polls
   * the same job is open, so both do not fetch concurrently.
   */
  pollEnabled?: boolean;
}

export function useRestoreJobs(initialFilters: UseRestoreJobsOptions = {}): UseRestoreJobsReturn {
  const [jobs, setJobs] = useState<RestoreJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialFilters.page || 1);
  const [timezone, setTimezone] = useState('UTC');
  const [filters, setFiltersState] = useState<RestoreJobsFilters>(initialFilters);

  const pageSize = 50;

  const fetchJobs = useCallback(
    async (background = false) => {
      try {
        if (!background) setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('page_size', pageSize.toString());

        if (filters.status) params.append('status', filters.status);
        if (filters.search) params.append('search', filters.search);
        if (filters.backup_job_id) params.append('backup_job_id', filters.backup_job_id);
        if (filters.agent_id) params.append('agent_id', filters.agent_id);

        const response = await api.get<RestoreJobsResponse>(`/restore/jobs?${params.toString()}`);

        setJobs(response.data.restore_jobs);
        setTotal(response.data.total || 0);
        setTimezone(response.data.timezone || 'UTC');
      } catch (err) {
        setError('Failed to load restore jobs');
        console.error('Error fetching restore jobs:', err);
      } finally {
        if (!background) setLoading(false);
      }
    },
    [page, filters]
  );

  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response (a `[jobs]` dependency would restart the timer each tick).
  const jobsRef = useRef<RestoreJob[]>([]);
  const fetchJobsRef = useRef<(background?: boolean) => Promise<void>>(async () => {});
  jobsRef.current = jobs;
  fetchJobsRef.current = fetchJobs;

  // Initial fetch and when page/filters change
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Auto-refresh while jobs are running. Adaptive: 5s base, backing off to 30s
  // once responses stop changing, and never while the tab is hidden. The old
  // fixed 2s interval cost 30 req/min per idle tab against a 1000/hour budget.
  useAdaptivePoll({
    enabled:
      initialFilters.pollEnabled !== false &&
      jobs.some((job) => job.status === 'RUNNING' || job.status === 'PENDING'),
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      await fetchJobsRef.current(true);
      return jobsRef.current
        .map((j) => `${j.id}:${j.status}:${j.progress_percent ?? ''}`)
        .join('|');
    },
    isEqual: (a, b) => a === b,
  });

  const setFilters = useCallback((newFilters: RestoreJobsFilters) => {
    setFiltersState(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    jobs,
    loading,
    error,
    total,
    page,
    timezone,
    fetchJobs,
    setPage: handleSetPage,
    setFilters,
  };
}
